/**
 * @typedef {(entry: IntersectionObserverEntry | { intersectionRatio: number, isIntersecting: boolean, target: Element }, observer: IntersectionObserver | null) => void} IntersectionHandler
 */

/**
 * @typedef {object} IntersectionManager
 * @property {(el: Element, handler: IntersectionHandler) => () => void} observe
 * @property {(el: Element, handler?: IntersectionHandler) => void} unobserve
 */

/** @type {Map<string, IntersectionManager>} */
const registry = new Map()

/** @returns {boolean} */
export function supportsIntersectionObserver() {
  return typeof window !== 'undefined' && 'IntersectionObserver' in window
}

/**
 * @param {number | number[] | undefined} t
 * @returns {number[] | undefined}
 */
function normalizeThreshold(t) {
  if (t === undefined) {
    return undefined
  }
  const arr = Array.isArray(t) ? t.slice() : [t]
  return Array.from(new Set(arr.filter((x) => typeof x === 'number'))).sort(
    (left, right) => left - right
  )
}

/**
 * @param {IntersectionObserverInit} options
 * @returns {string}
 */
function keyFor(options) {
  const rootId = options.root instanceof Element ? options.root.tagName : 'null'
  const margin = options.rootMargin || ''
  const threshold = normalizeThreshold(options.threshold) || []
  return `${rootId}|${margin}|${threshold.join(',')}`
}

/**
 * @param {IntersectionObserverInit} [options]
 * @returns {IntersectionManager}
 */
export function useIntersection(options = {}) {
  const normalized = {
    ...options,
    threshold: normalizeThreshold(options.threshold)
  }
  const key = keyFor(normalized)

  const existing = registry.get(key)
  if (existing) {
    return existing
  }

  if (!supportsIntersectionObserver()) {
    const handlers = new Map()
    const manager = {
      observe(el, handler) {
        const set = handlers.get(el) || new Set()
        set.add(handler)
        handlers.set(el, set)
        queueMicrotask(() => {
          try {
            handler(
              {
                intersectionRatio: 1,
                isIntersecting: true,
                target: el
              },
              null
            )
          } catch {}
        })
        return () => manager.unobserve(el, handler)
      },
      unobserve(el, handler) {
        const set = handlers.get(el)
        if (!set) {
          return
        }
        if (handler) {
          set.delete(handler)
        }
        if (!handler || set.size === 0) {
          handlers.delete(el)
        }
      }
    }
    registry.set(key, manager)
    return manager
  }

  const targets = new Map()
  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      const handlers = targets.get(entry.target)
      if (!handlers) {
        continue
      }
      handlers.forEach((handler) => {
        try {
          handler(entry, observer)
        } catch {}
      })
    }
  }, normalized)

  const manager = {
    observe(el, handler) {
      let set = targets.get(el)
      if (!set) {
        set = new Set()
        targets.set(el, set)
        observer.observe(el)
      }
      set.add(handler)
      return () => manager.unobserve(el, handler)
    },
    unobserve(el, handler) {
      const set = targets.get(el)
      if (!set) {
        return
      }
      if (handler) {
        set.delete(handler)
      }
      if (!handler || set.size === 0) {
        targets.delete(el)
        observer.unobserve(el)
      }
    }
  }

  registry.set(key, manager)
  return manager
}
